"use client";

import { useState } from "react";
import { Offers } from "@pamperedchef/design-system";
import * as styles from "./ProductMessages.module.scss";

// CMS icon names differ from the DS icon set for a few entries.
const OFFERS_ICON_ALIAS: Record<string, string> = {
	truck: "delivery-color"
};

function toOffersIcon(name?: string): { name: string } | undefined {
	if (!name) return undefined;
	return { name: OFFERS_ICON_ALIAS[name] ?? name };
}

/**
 * Owned by this composite rather than imported from the app's catalog domain,
 * so it stays packageable and can be lifted out of consumer-web.
 */
export interface ProductMessage {
	text: string;
	icon?: string;
	link?: { label: string; href: string };
}

export interface ProductMessagesProps {
	/**
	 * Shopper-facing message rows — free shipping, host-party offers, delivery
	 * notes. Authored order is display order; callers resolve any date window.
	 */
	messages: ProductMessage[];
	/**
	 * Beyond this many, the remainder collapses behind a "+N More Deals"
	 * toggle. Default 2, matching what the CMS tells authors to expect.
	 */
	collapseAfter?: number;
	className?: string;
}

/**
 * Stacks the product's message rows below the price block, above Add to Cart.
 */
export function ProductMessages({
	messages,
	collapseAfter = 2,
	className
}: ProductMessagesProps) {
	const [expanded, setExpanded] = useState(false);

	if (!messages.length) return null;

	const collapsible = messages.length > collapseAfter;
	const visible =
		!collapsible || expanded ? messages : messages.slice(0, collapseAfter);
	const hiddenCount = messages.length - collapseAfter;

	return (
		<div
			className={[styles["product-messages"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="ProductMessages"
		>
			{visible.map((message, idx) => (
				<div
					key={`${message.text}-${idx}`}
					className={styles["product-messages__item"]}
				>
					<Offers
						surface="default"
						text={message.text}
						icon={toOffersIcon(message.icon)}
						textLink={
							message.link
								? {
										children: message.link.label,
										href: message.link.href
									}
								: undefined
						}
					/>
				</div>
			))}
			{collapsible && !expanded ? (
				<button
					type="button"
					className={styles["product-messages__toggle"]}
					onClick={() => setExpanded(true)}
				>
					{`+${hiddenCount} More Deals`}
				</button>
			) : null}
		</div>
	);
}
